import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { useAuth, formatApiError } from '../contexts/AuthContext';

export const AdminLogin = () => {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'Admin Login | Toronto Royale';
  }, []);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'rgba(225,225,225,0.8)', fontSize: '13px', letterSpacing: '2px', textTransform: 'uppercase' }}>Loading…</p>
      </div>
    );
  }
  if (user) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email, password);
      navigate('/admin', { replace: true });
    } catch (err) {
      if (err.response?.data?.detail) {
        setError(formatApiError(err.response.data.detail));
      } else if (err.response) {
        setError(`Server error (HTTP ${err.response.status}). Please try again.`);
      } else if (err.request) {
        setError('Cannot reach the server. Check your connection and try again.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-page" style={{ minHeight: '100vh', backgroundColor: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 30%, rgba(194,168,120,0.06) 0%, transparent 60%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', inset: 0, opacity: 0.025, backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E\")", pointerEvents: 'none' }} />

      <form onSubmit={handleSubmit} data-testid="admin-login-form" style={{ width: '100%', maxWidth: '420px', position: 'relative', zIndex: 10, backgroundColor: 'rgba(14,14,14,0.6)', border: '1px solid rgba(194,168,120,0.1)', padding: '56px 44px', backdropFilter: 'blur(12px)', boxShadow: '0 24px 80px rgba(0,0,0,0.5)' }}>
        <div style={{ position: 'absolute', top: 0, left: '15%', right: '15%', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.35), transparent)' }} />
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <p style={{ fontSize: '10px', color: 'rgba(194,168,120,0.6)', letterSpacing: '5px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '14px' }}>Toronto Royale</p>
          <h1 className="font-serif" style={{ fontSize: '26px', fontWeight: 500, color: '#FAFAFA', letterSpacing: '0.5px' }}>Admin Console</h1>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '11px', color: 'rgba(194,168,120,0.7)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '10px', fontWeight: 400 }}>Email</label>
          <input
            data-testid="admin-login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            style={{ width: '100%', backgroundColor: 'rgba(8,8,8,0.7)', border: '1px solid rgba(194,168,120,0.15)', color: '#F5F5F5', padding: '13px 16px', fontSize: '14px', outline: 'none', transition: 'border-color 200ms ease' }}
            onFocus={(e) => { e.target.style.borderColor = 'rgba(194,168,120,0.4)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(194,168,120,0.15)'; }}
          />
        </div>

        <div style={{ marginBottom: '32px' }}>
          <label style={{ display: 'block', fontSize: '11px', color: 'rgba(194,168,120,0.7)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '10px', fontWeight: 400 }}>Password</label>
          <input
            data-testid="admin-login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            style={{ width: '100%', backgroundColor: 'rgba(8,8,8,0.7)', border: '1px solid rgba(194,168,120,0.15)', color: '#F5F5F5', padding: '13px 16px', fontSize: '14px', outline: 'none', transition: 'border-color 200ms ease' }}
            onFocus={(e) => { e.target.style.borderColor = 'rgba(194,168,120,0.4)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(194,168,120,0.15)'; }}
          />
        </div>

        {error && (
          <div data-testid="admin-login-error" style={{ marginBottom: '20px', padding: '12px 14px', backgroundColor: 'rgba(180,40,40,0.08)', border: '1px solid rgba(220,80,80,0.25)', color: '#E89B9B', fontSize: '12px', letterSpacing: '0.3px' }}>
            {error}
          </div>
        )}

        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={submitting}
          style={{
            width: '100%', padding: '14px', backgroundColor: '#C2A878', color: '#0A0A0A',
            fontSize: '12px', fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase',
            border: 'none', cursor: submitting ? 'wait' : 'pointer',
            opacity: submitting ? 0.6 : 1, transition: 'all 200ms ease',
          }}
          onMouseEnter={(e) => { if (!submitting) e.target.style.backgroundColor = '#D4B888'; }}
          onMouseLeave={(e) => { if (!submitting) e.target.style.backgroundColor = '#C2A878'; }}
        >
          {submitting ? 'Signing in…' : 'Sign In'}
        </button>

        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <Link
            to="/admin/forgot-password"
            data-testid="admin-forgot-password-link"
            style={{ fontSize: '12px', color: 'rgba(194,168,120,0.6)', textDecoration: 'none', letterSpacing: '0.5px', transition: 'color 180ms ease' }}
            onMouseEnter={(e) => { e.target.style.color = '#C2A878'; }}
            onMouseLeave={(e) => { e.target.style.color = 'rgba(194,168,120,0.6)'; }}
          >
            Forgot your password?
          </Link>
        </div>
      </form>
    </div>
  );
};
